import { OneOrManySchema, QueryBooleanSchema } from 'notable-common';
import { z } from 'zod';
import { FlowKind, FlowState } from './enums.js';
import { FlowPricingMode } from './pricingMode.js';
import { FlowTagInputsSchema, FlowTagNameSchema } from './tags.js';
import { FlowVersionTarget } from './versionTarget.js';
import { FlowDefinitionState } from '../flowDefinition/flowDefinition.js';
import { FlowApiPageLimitSchema, FlowApiPageOffsetSchema } from '../pagination/pageLimit.js';
export var FlowOrderByFieldName;
(function (FlowOrderByFieldName) {
    FlowOrderByFieldName["Name"] = "name";
    FlowOrderByFieldName["State"] = "state";
    FlowOrderByFieldName["Description"] = "description";
    FlowOrderByFieldName["Created"] = "created";
    FlowOrderByFieldName["Updated"] = "updated";
    FlowOrderByFieldName["CreatedByEmail"] = "createdByEmail";
    FlowOrderByFieldName["AgentName"] = "agentName";
    FlowOrderByFieldName["AgentId"] = "agentId";
})(FlowOrderByFieldName || (FlowOrderByFieldName = {}));
export var FlowRunStatsMode;
(function (FlowRunStatsMode) {
    FlowRunStatsMode["Summary"] = "Summary";
    FlowRunStatsMode["None"] = "None";
})(FlowRunStatsMode || (FlowRunStatsMode = {}));
const FlowOrderByFieldNameSchema = z.enum(FlowOrderByFieldName);
const FlowOrderByFieldWithDirectionSchema = z.object({
    field: FlowOrderByFieldNameSchema,
    direction: z.enum(['asc', 'desc']).default('asc'),
});
const IsoDateTimeFilterSchema = z.iso.datetime({ offset: true });
export const FlowFindAllQueryParamsSchema = z.object({
    q: z.string().optional(),
    name: z.string().optional(),
    slug: OneOrManySchema(z.string()).optional(),
    ids: z.union([z.array(z.uuid()), z.uuid()]).optional(),
    organizationId: z.union([z.array(z.uuid()), z.uuid()]).optional(),
    agentId: z.union([z.array(z.uuid()), z.uuid()]).optional(),
    state: z.enum(FlowState).optional(),
    kind: z.enum(FlowKind).optional(),
    includeInvisible: QueryBooleanSchema.default(false).optional(),
    createdBefore: IsoDateTimeFilterSchema.optional(),
    createdAfter: IsoDateTimeFilterSchema.optional(),
    createdByEmail: z.string().optional(),
    includeEmails: QueryBooleanSchema.optional(),
    includesSubFlowDefinitionId: z.uuid().optional(),
    includeTags: QueryBooleanSchema.default(false).optional(),
    tags: z.union([z.array(FlowTagNameSchema), FlowTagNameSchema, FlowTagInputsSchema]).optional(),
    includeFlowDefinitionStates: QueryBooleanSchema.default(false).optional(),
    runStatsMode: z
        .enum(FlowRunStatsMode)
        .optional()
        .describe('How to include run statistics in each flow.'),
    flowDefinitionStates: OneOrManySchema(z.string()).optional(),
    limit: FlowApiPageLimitSchema.describe('How many flows to return.').optional(),
    offset: FlowApiPageOffsetSchema.describe('How many matches to skip, for paging.').optional(),
    orderBy: z
        .union([
        // FlowOrderByFieldNameSchema is the old format (string) for backward compatibility
        // will be removed once frontend is updated to use the new format
        OneOrManySchema(FlowOrderByFieldNameSchema),
        OneOrManySchema(FlowOrderByFieldWithDirectionSchema),
    ])
        .optional(),
    // This is not needed anymore, but keeping it for backward compatibility with FlowOrderByFieldNameSchema
    // will be removed once frontend is updated to use the new format
    orderDirection: z.enum(['asc', 'desc']).optional(),
});
/**
 * Path params for the routes addressing one flow: get and update.
 *
 * Named for what it carries rather than for either route, since both use it.
 */
export const FlowIdParamsSchema = z.strictObject({ id: z.uuid() });
export const GetFlowByIdQueryParamsSchema = z.object({
    flowDefinitionLimit: FlowApiPageLimitSchema.describe('Maximum number of flow definition metadata records to return.').optional(),
    flowDefinitionOrderDirection: z
        .enum(['asc', 'desc'])
        .describe('Order flow definition metadata by version, ascending or descending.')
        .optional(),
});
/**
 * Body `POST /flows` accepts. Structural validation only: flow-api additionally checks that the
 * name is free within the organization and that the agent exists and belongs to it, both of which
 * need the database.
 */
export const FlowCreateBodySchema = z
    .object({
    name: z.string(),
    organizationId: z.string().describe('Organization the flow belongs to.'),
    kind: z.enum(FlowKind).optional().default(FlowKind.Base),
    agentId: z.uuid().describe('Agent the flow runs under.'),
    state: z.enum(FlowState).optional().default(FlowState.Active),
    description: z.string().optional(),
    tags: FlowTagInputsSchema.nullish(),
    pricingMode: z.enum(FlowPricingMode).optional(),
})
    .strict();
/**
 * Body `PATCH /flows/{id}` accepts.
 *
 * The kind is not among them: a flow that became a Skill or a template would be a different thing
 * to every caller holding its id.
 */
export const FlowUpdateBodySchema = z.object({
    name: z.string().optional(),
    description: z.string().optional(),
    state: z.enum(FlowState).optional(),
    organizationId: z.uuid().optional(),
    agentId: z.uuid().optional(),
    tags: FlowTagInputsSchema.nullish(),
    pricingMode: z.enum(FlowPricingMode).optional(),
});
/** Path parameters `GET /block/skills/{id}` accepts. A skill is a flow of that kind. */
export const FlowSkillIdParamsSchema = z.object({
    id: z.uuid().describe('The skill.'),
});
/**
 * Query parameters `GET /flows/{id}/subflow-version` accepts, which resolves the definition a
 * subflow block would run.
 *
 * Naming a definition answers for that one; naming a target resolves it the way a run would, such
 * as the published version.
 */
export const FlowSubflowVersionQuerySchema = z.object({
    target: z
        .enum(FlowVersionTarget)
        .describe('Which definition of the subflow to resolve, such as the published one.')
        .optional(),
    flowDefinitionId: z.uuid().describe('Resolve this definition instead of a target.').optional(),
});
export const FlowSkillsFindAllQueryParamsSchema = z.object({
    q: z.string().optional(),
    name: z.string().optional(),
    slug: z.string().optional(),
    ids: z.union([z.array(z.uuid()), z.uuid()]).optional(),
    organizationId: z.union([z.array(z.uuid()), z.uuid()]).optional(),
    agentId: z.union([z.array(z.uuid()), z.uuid()]).optional(),
    state: z.enum(FlowState).optional(),
    kind: z.enum(FlowKind).optional(),
    createdBefore: IsoDateTimeFilterSchema.optional(),
    createdAfter: IsoDateTimeFilterSchema.optional(),
    createdByEmail: z.string().optional(),
    includeTags: QueryBooleanSchema.default(false).optional(),
    tags: z.union([z.array(FlowTagNameSchema), FlowTagNameSchema]).optional(),
    flowDefinitionStates: OneOrManySchema(z.enum(FlowDefinitionState)).optional(),
    limit: FlowApiPageLimitSchema.describe('How many skills to return.').optional(),
    offset: FlowApiPageOffsetSchema.describe('How many matches to skip, for paging.').optional(),
    orderBy: OneOrManySchema(FlowOrderByFieldWithDirectionSchema).optional(),
});
//# sourceMappingURL=flowRequests.js.map